import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CarrierDataService } from 'src/app/shared/services/carrier-data.service';
import { PublicCarrier } from 'src/app/shared/models/public-carrier';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private carrierDataSubject = new BehaviorSubject<PublicCarrier | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private isLoadedSubject = new BehaviorSubject<boolean>(false);

  public carrierData$ = this.carrierDataSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public isLoaded$ = this.isLoadedSubject.asObservable();

  constructor(private carrierDataService: CarrierDataService) {}

  get carrierData(): PublicCarrier | null {
    return this.carrierDataSubject.value;
  }

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  get isLoaded(): boolean {
    return this.isLoadedSubject.value;
  }

  loadCarrierData(): Observable<PublicCarrier | null> {
    // If already loaded, return the cached data
    if (this.isLoadedSubject.value && this.carrierDataSubject.value) {
      return this.carrierData$;
    }

    // If already loading, return the current observable
    if (this.isLoadingSubject.value) {
      return this.carrierData$;
    }

    const carrierCompanyId = sessionStorage.getItem('carrierCompanyId');
    const publishableKey = sessionStorage.getItem('publishableKey');

    if (!carrierCompanyId || !publishableKey) {
      this.carrierDataSubject.next(null);
      this.isLoadedSubject.next(true);
      return this.carrierData$;
    }

    this.isLoadingSubject.next(true);

    this.carrierDataService.getCarrierPublicById(carrierCompanyId).subscribe({
      next: (response) => {
        this.carrierDataSubject.next(response);
        this.isLoadedSubject.next(true);
        this.isLoadingSubject.next(false);
      },
      error: (err) => {
        console.error('Error fetching carrier data', err);
        this.carrierDataSubject.next(null);
        this.isLoadedSubject.next(true);
        this.isLoadingSubject.next(false);
      }
    });

    return this.carrierData$;
  }

  // Method to manually set carrier data (useful for testing or if data comes from another source)
  setCarrierData(carrierData: PublicCarrier | null): void {
    this.carrierDataSubject.next(carrierData);
    this.isLoadedSubject.next(true);
  }

  // Method to clear the data (useful when user navigates away from onboarding)
  clearCarrierData(): void {
    this.carrierDataSubject.next(null);
    this.isLoadedSubject.next(false);
    this.isLoadingSubject.next(false);
  }
}
